import { useState, useEffect } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import router from 'next/router';
import { useSettings } from 'src/@core/hooks/useSettings';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { SalesOrderDto } from 'src/models/salesorder';
import { useGetSalesOrderRevenueQuery } from 'src/queries/services/salesorder';
import { getSalesOrders } from 'src/services/salesorder';
import buildFilterString from '../../helpers/buildFilterString';

const widgetTitle = 'Total Revenue';

const TotalRevenueFigureWidget = ({ dashboardDateStrings = [[''], ['']], orderByValue = '' }) => {
    // State
    const [totalRevenue, setTotalRevenue] = useState<number>(0);
    const [orderIdArray, setOrderIdArray] = useState<string[] | undefined>(undefined);   
    
    // Hooks
    const settings = useSettings();
    const theme = useTheme();
    const { t } = useVigoTranslation();

  /**
   * Get Sales Orders Query
   * Fetches all sales orders for the given orderByValue and date range
   */
  const { data: salesOrdersData, isSuccess: isSalesOrdersSuccess } = useQuery({
    queryKey: [
      'totalRevenueFigureWidget',
      buildFilterString(orderByValue, dashboardDateStrings),
      orderByValue,
      dashboardDateStrings
    ],
    queryFn: async () => {
      const res = await getSalesOrders({ filter: buildFilterString(orderByValue, dashboardDateStrings) });

      return res.data;
    },
    enabled: true
  });

    // Handle sales orders data changes
    useEffect(() => {
      if (!isSalesOrdersSuccess || !salesOrdersData) return;

      const orderIds = (salesOrdersData as SalesOrderDto[])?.map(order => order?.id ?? '');

      if (orderIds.length === 0) {
        setTotalRevenue(0);

        return;
      }

      setOrderIdArray(orderIds);
    }, [salesOrdersData, isSalesOrdersSuccess]);

    /**
     * Order Revenue Query
     * Fetches all sales order revenue for sales orders with ids in the orderIdArray
     */
    const filterString = orderIdArray?.map((id: string) => `salesOrderId eq guid'${id}'`).join(' or ') ?? '';
    
    const { data: revenueData, isSuccess: isRevenueSuccess, isFetching: isRevenueFetching } = useGetSalesOrderRevenueQuery({
      params: {
        filter: filterString
      },
      enabled: Boolean(orderIdArray && orderIdArray.length > 0),
      queryKeyOverride: ['salesOrderRevenue', filterString]
    });

    // Handle revenue data changes
    useEffect(() => {
      if (!orderIdArray || !isRevenueSuccess || isRevenueFetching) return;

      if (!revenueData || revenueData.length === 0) {
        setTotalRevenue(0);
        setOrderIdArray(undefined);
        
        return;
      }

      const total = revenueData.reduce((acc, curr) => acc + (curr?.value ?? 0), 0);
        setTotalRevenue(total);
        setOrderIdArray(undefined);
    }, [revenueData, orderIdArray, isRevenueSuccess, isRevenueFetching]);

    function onClick() {
        if (!settings.settings.unlockDashboardLayout) {
            router.push(
                `/uiflow/consignment?filter=${buildFilterString(
                    orderByValue,
                    dashboardDateStrings
                )}&extrafilter=isDeleted eq false`
            );
        }
    }

    return (
        <Stack
            direction="column"
            sx={{
                height: '100%', 
                backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e',
                borderRadius: '5px',
                cursor: 'pointer',
                position: 'relative'
            }}>
            <Typography
                sx={{
                    marginTop: '10px',
                    marginLeft: '10px',
                    textAlign: 'left',
                    fontSize: 'clamp(1rem, 10cqw, 1.5rem)',
                    fontWeight: '600'
                }}>
                {t(widgetTitle)}
            </Typography>
            
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%'
                }}>
                <VigoTypography
                    value={`£${Number(totalRevenue).toFixed(2)}`}
                    onClick={onClick}
                    className="totalRevenueFigure"
                    sx={{
                        fontSize: 'clamp(1rem, 30cqw, 3.714rem) !important',
                        fontWeight: '500', 
                        cursor: 'pointer',
                        textAlign: 'center'
                    }}
                    disableTranslation
                />
            </Box>
        </Stack>
    );
   };

export default TotalRevenueFigureWidget;